/**
 * @file shop-integration-meta.ts
 * @description Интеграции по магазину: подписи политики и источника,
 * доступные действия (phase19.28 этап А; uiux-platform-shops §6; PL13).
 *
 * PL13: вид «только своя» (перевозчики, Avito, VK, Telegram, касса…) общую
 * настройку платформы молча не подставляет — если она есть, её нужно
 * перенести в магазин. Вид «общая допустима» (DaData, почта, хранилище)
 * работает на общей, пока магазин не завёл свою.
 *
 * Перенос на странице «Общие интеграции»: признак `needsTransfer` у элемента
 * `GET /admin/platform/integrations` (API считает его по `policy` вида).
 */

import type { ShopStatusMeta } from "../shop/status-meta";
import { INTEGRATION_KIND_META, type PlatformIntegrationDto, type PlatformIntegrationKind } from "../api-platform-integrations";
import type { IntegrationPolicy, IntegrationScope, ShopIntegrationItem } from "../api-platform-shops";

export const POLICY_LABEL: Readonly<Record<IntegrationPolicy, string>> = {
  ONLY_OWN: "только своя",
  SHARED_ALLOWED: "общая допустима",
};

/** Источник настройки → пилюля. Для «только своя» общая — ошибка: она не работает. */
export function scopeMeta(item: Pick<ShopIntegrationItem, "scope" | "policy" | "isActive">): ShopStatusMeta {
  const META: Record<IntegrationScope, ShopStatusMeta> = {
    SHOP: item.isActive ? { label: "Своя · активна", tone: "success" } : { label: "Своя · выключена", tone: "attention" },
    PLATFORM:
      item.policy === "ONLY_OWN"
        ? { label: "Общая · не подставляется", tone: "error" }
        : { label: "Общая платформы", tone: "progress" },
    NONE: { label: "Не подключена", tone: "neutral" },
  };
  return META[item.scope];
}

/** Действие карточки интеграции магазина. */
export type ShopIntegrationAction = "edit" | "own" | "connect" | "transfer";

export function shopIntegrationActions(item: ShopIntegrationItem): ShopIntegrationAction[] {
  if (item.scope === "SHOP") return ["edit"];
  if (item.scope === "NONE") return ["connect"];
  return item.policy === "ONLY_OWN" && item.integrationId ? ["transfer", "own"] : ["own"];
}

export const ACTION_LABEL: Readonly<Record<ShopIntegrationAction, string>> = {
  edit: "Изменить",
  own: "Своя настройка",
  connect: "Подключить",
  transfer: "Перенести общую в магазин",
};

/** Общая интеграция вида «только своя» — кандидат на перенос (признак API `needsTransfer`). */
export function needsTransfer(item: PlatformIntegrationDto): boolean {
  return item.needsTransfer === true;
}

/** Вид известен кабинету — есть форма ключей. */
export function isKnownKind(kind: string): kind is PlatformIntegrationKind {
  return Object.hasOwn(INTEGRATION_KIND_META, kind);
}
